import { useState } from "react"
import useError from "../../../hooks/useError"
import rest from "../../../rest"

export interface UserType {
    id?: number
    username?: string
    first_name?: string
    last_name?: string
    email?: string
    is_superuser?: boolean
    is_staff?: boolean
    is_active?: boolean
    groups?: {id: number; name: string}[]
    user_permissions?: {id: number; name: string; codename: string}[]
    acl_user?: ACLType[]
}

export interface GroupType {
    id?: number;
    name?: string;
}

export interface PermissionType {
    id?: number;
    name?: string;
	codename?: string;
}

export interface ACLType {
    id: number
    group: number
    permission: number
	user: number
    filtros: {chave: string; valor: string}[]
	metaACL: MetaACLType
}

export interface MetaACLType {
    group: number;
    permission: number;
    filtros: {
		chave: string,
		nome: string,
		dominio:{[id:number]:string}
	}[]
}

export const useUserStore=() => {
    /*
	const initialStore: UserType={
		username: '',
		email: '',
	}
	const [store, _setStore]=useState<UserType>(initialStore)
    const setStore=(obj: UserType) => {
		_setStore(Object.assign({}, store, obj))
	}
    */

    const { callRest, checkLoading, checkSucess, checkError } = useRest()

	const list=async (page: number, searchString?: string) => {
        let search = ''
        if (searchString) {
            search = '&search='+searchString
        }
		const json: { results: UserType[], num_pages: number } =
			await callRest(`user/?page_size=100&page=${page}${search}`, 'list')
		return json
	}

	const get=async (id: number) => {
		const json: UserType = await callRest(`user/${id}/`, 'get')
		return json
	}

    const saveGroup=async (
        userId: number,
        groupId: number,
        filtros: {[chave: string]:string},
        acls: ACLType[],
    ) => {
        const acls_ = acls.filter(acl => !acl.filtros.find(f => !f.valor))
		return await callRest(
            `user/${userId}/group/`,
            'save-group',
            'POST',
            {'group': groupId, 'filtros': filtros, 'acls': acls_},
        )
    }

	const savePermission=async (
        userId: number,
        permissionId: number,
        filtros: {[chave: string]:string},
        acls: ACLType[],
    ) => {
		return await callRest(
            `user/${userId}/permission/`,
            'save-permission',
            'POST',
            {'permission': permissionId, 'filtros': filtros, 'acls': acls},
        )
    }

    const deleteGroup=async (userId: number, groupId: number) => {
		return await callRest(
            `user/${userId}/group/${groupId}/`,
            'delete-group',
            'DELETE',
        )
    }

	const deletePermission=async (userId: number, permissionId: number) => {
		return await callRest(
            `user/${userId}/permission/${permissionId}/`,
            'delete-permission',
            'DELETE',
        )
    }

	return { list, get, checkLoading, saveGroup, deleteGroup, savePermission, deletePermission, checkSucess, checkError }
}

export const useGroupStore=() => {

    const { callRest } = useRest()

	const list=async () => {
		const json: { results: GroupType[] } =
			await callRest(`group/?page_size=1000`, 'list')
		return json
	}

    const get=async (id: number) => {
		const json: GroupType = await callRest(`group/${id}/`, 'get')
		return json
	}

	return { list, get }
}

export const usePermissionStore=() => {

    const { callRest } = useRest()

	const list=async () => {
		const json: { results: PermissionType[] } =
			await callRest(`permission/?page_size=1000`, 'list')
		return json
	}

    const get=async (id: number) => {
		const json: PermissionType = await callRest(`permission/${id}/`, 'get')
		return json
	}

	return { list, get }
}

export const useACLStore=() => {

    const { callRest } = useRest()

	const list=async (
		{ user, group, permission }:
		{user?: number, group?: number, permission?: number }
	) => {
        let query = ''
        if (user) {
            query += '&user='+user
        }
        if (group) {
            query += '&group='+group
        }
        if (permission) {
            query += '&permission='+permission
        }
		const json: { results: ACLType[] } =
			await callRest(`acl/?page_size=1000${query}`, 'list')
		return json
	}

	return { list }
}

export const useMetaACLStore=() => {

    const { callRest } = useRest()

	const list=async () => {
		const json: { results: MetaACLType[] } =
			await callRest(`meta-acl/?page_size=1000`, 'list')
		return json
	}

	const aclFilter = (metaACLs: MetaACLType[], acl: ACLType | undefined, chave: string) => {
		let ma: MetaACLType | undefined = metaACLs[0]
		if (acl) {
			if (acl.group) {
				ma = metaACLs.find(
					ma => ma.group === acl.group && !ma.permission
				)	
			}
			if (acl.permission) {
				ma = metaACLs.find(
					ma => ma.permission === acl.permission && !ma.group
				)	
			}
		}
        if (!ma) {
            return undefined
        }
        const f = ma.filtros.find(f => f.chave === chave)
        if (!f || !f.nome) {
            return undefined
        }
        return f
    }


	const labelACLFilter = (metaACLs: MetaACLType[], acl: ACLType | undefined, chave: string) => {
		const f = aclFilter(metaACLs, acl, chave)
        if (!f || !f.nome) {
            return chave
        }
        return f.nome
    }

	const valueACLFilter = (metaACLs: MetaACLType[], acl: ACLType | undefined, chave: string, valor: string) => {
        const f = aclFilter(metaACLs, acl, chave)
        if (!f || !f.nome) {
            return valor
        }
        return f.dominio[parseInt(valor)] || valor
    }

	const dominioACLFilter = (metaACLs: MetaACLType[], acl: ACLType | undefined, chave: string) => {
		const f = aclFilter(metaACLs, acl, chave)
        if (!f || !f.dominio) {
            return undefined
        }
        return f.dominio
    }

	return { list, labelACLFilter, valueACLFilter, dominioACLFilter }
}

const useRest=() => {
	const [success, setSuccess]=useState('')
	const [loading, setLoading]=useState('')
    const { error, setError } = useError()

	const checkSucess=() => success

	const checkError=(field: string): string|null => {
		if (error && Object.keys(error).length > 0) {
            let fieldError = error
            if (error[field]) {
                fieldError=error[field]
            }
			if (Array.isArray(fieldError)) {
				return fieldError[0]
			} else {
				return JSON.stringify(fieldError)
			}
		} else {
			return null
		}
	}

    const checkLoading=(action: string) => loading===action

	const clearError=() => {
		setError({})
		setSuccess('')
	}

	const callRest=async (
        resource: string,
        loading: string,
        method?: string,
        body?: any,
    ) => {
		try {
			clearError()
			setLoading(loading)
      		const json: any = await rest(resource, method, body)
			setLoading('')
            if (json.detail) {
                setSuccess(json.detail)
            }
			return json
		} catch (err) {
			setError(err)
			setLoading('')
			throw err
		}
	}

    return { callRest, checkSucess, checkError, checkLoading }
}